import { useRef } from 'react'
import type { AddDictionaryWordOutcome } from '@/api/types/dictionary'
import {
  type SpellCheckState,
  type SpellIssue,
  changeOccurrence,
  findOpenOccurrence,
  markIssueAdded,
  wordsMatch,
} from '@/lib/spell-check/spell-check-state'
import { notifyPopoverSaveFailed } from '../handlers/notify-dictionary-change-failed'

interface UsePopoverDictionaryAddOptions {
  state: SpellCheckState
  onStateChange: (next: SpellCheckState) => void
  /** Saves a custom word and reports how it ended; the dictionary cache changes only on success. */
  onAddWord: (word: string) => Promise<AddDictionaryWordOutcome>
}

/** What a Change & Add did: the word that was changed and the state it left. */
export interface ChangedAndAdded {
  issueId: string
  next: SpellCheckState
}

/**
 * The fix popover's two ways of adding to the dictionary (SC-16): Add "word" to Dictionary and
 * Change & Add to Dictionary. Both wait for the backend before touching the text or the issue
 * list, so a failed save leaves the word flagged and the text as it was, and tells the user. The
 * save takes a moment and the panel keeps rendering while it runs, so what follows reads the
 * latest state and handler, not the ones the click saw.
 */
export function usePopoverDictionaryAdd({
  state,
  onStateChange,
  onAddWord,
}: UsePopoverDictionaryAddOptions) {
  const latestRef = useRef({ state, onStateChange })

  latestRef.current = { state, onStateChange }

  // True once the word is in the dictionary. A word the dictionary already holds under the Case
  // Sensitive setting is not sent again: it is flagged only because the Custom Dictionary toggle is
  // off, or someone just added it. A save that fails tells the user why (`changesText` is a Change &
  // Add, whose failure also says the text was left as it was).
  const saveWord = async (word: string, changesText: boolean): Promise<boolean> => {
    const { state: current } = latestRef.current

    if (current.customWords.some((w) => wordsMatch(w, word, current.caseSensitive))) return true

    const outcome = await onAddWord(word)

    if (outcome.status === 'added' || outcome.status === 'duplicate') return true

    notifyPopoverSaveFailed({ reason: outcome.status, word, changesText })

    return false
  }

  /** Adds the flagged word; true once it is saved and its instances have left the list. */
  const addToDictionary = async (issue: SpellIssue): Promise<boolean> => {
    if (!(await saveWord(issue.word, false))) return false

    const { state: current, onStateChange: change } = latestRef.current
    const found = current.issues.find((i) => wordsMatch(i.word, issue.word, current.caseSensitive))

    if (found) change(markIssueAdded(current, found.id))

    return true
  }

  /**
   * Changes the one instance the popover is on to `correction` and adds `correction` to the
   * dictionary. The word is saved first. Then the instance is found again by where it sits, since
   * the text may have moved during the save, and nothing is changed if its word is no longer
   * there. `null` when nothing was changed.
   */
  const changeAndAdd = async (
    issue: SpellIssue,
    occurrenceIndex: number,
    correction: string
  ): Promise<ChangedAndAdded | null> => {
    if (!(await saveWord(correction, true))) return null

    const { state: current, onStateChange: change } = latestRef.current
    const found = findOpenOccurrence(current.issues, issue, occurrenceIndex, current.caseSensitive)

    if (!found) return null

    const next = changeOccurrence(current, found.issueId, found.index, correction)

    change(next)

    return { issueId: found.issueId, next }
  }

  return { addToDictionary, changeAndAdd }
}
